import { original, createReducer, Reducer, current } from '@reduxjs/toolkit';
import { createPatch } from 'rfc6902';
import getDiffPatch from '../patches/getDiffPatch';
import rebasePatch from '../patches/rebasePatch';
import type { EditorState } from './types';
import applyPatch from '../patches/applyPatch';
import { setPropertyByPath } from '../composer/utils/objects';
import {
  editDescriptor,
  initializeEditor,
  initializeEditorFailed,
  navigateEditor,
  rebaseEditorProject,
  redoLatestUndo,
  resetInitialMigrationDiff,
  revertCurrentDescriptor,
  saveProjectFailure,
  saveProjectPending,
  saveProjectSuccess,
  setMobileServiceConfig,
  undoLatestChange,
} from './actions';
import applyPatchIgnoringErrors from '../patches/applyPatchIgnoringErrors';

type DescriptorMigrator<Descriptor> = (descriptor: Descriptor) => Descriptor;
type DescriptorPostProcessor<Descriptor> = (newDescriptor: Descriptor, oldDescriptor: Descriptor) => Descriptor;

export const initialState: EditorState = {
  projectId: null,
  projectType: null,
  savingVersion: null,
  version: null,
  remoteDescriptor: null,
  currentDescriptor: null,
  initialMigrationDiffPatch: null,
  conflictVersion: null,
  editable: false,
  undoStack: [],
  redoStack: [],
  failedToInitialize: false,
  navigationPath: null,
  mobileServiceConfig: null,
};

const undoSplitThreshold = 1000;
const maxUndoStackLength = 100;

interface EditorReducerOptions<Descriptor> {
  /**
   * These "migrations" are done immediately when the descriptor is loaded, before the user starts editing it.
   * It should be used to ENSURE THAT APP EDITOR WORKS WITH THE DESCRIPTOR. Therefore you should _avoid_
   * making unnecessary changes as they would show the "Save" button even before user makes any actual changes.
   */
  migrateDescriptor?: DescriptorMigrator<Descriptor>;
  /**
   * These changes are done after every single edit of the descriptor. It should be used for e.g. cleanups.
   * The logic here *must* be fast enough even for big descriptors, as it's run after every single edit.
   */
  postProcessDescriptor?: DescriptorPostProcessor<Descriptor>;
}

const noOpProcessor = (descriptor: unknown) => descriptor;

/**
 * Create a Redux reducer for the descriptor editor state for a certain descriptor format.
 *
 * This allows re-using the same Redux reducer and actions on both SAP Build Apps and VCF editor,
 * but with some differences, particularly related to typing and what kind of "side-effects", e.g. cleanups,
 * would be made to the descriptor whenever it's edited by the user.
 */
export function createEditorReducer<Descriptor>(
  options: EditorReducerOptions<Descriptor> = {},
): Reducer<EditorState<Descriptor>> {
  const {
    migrateDescriptor = noOpProcessor as DescriptorMigrator<Descriptor>,
    postProcessDescriptor = noOpProcessor as DescriptorPostProcessor<Descriptor>,
  } = options;
  const reducer = createReducer(initialState, (builder) =>
    builder
      /**
       * Action for initializing the descriptor editor state with a successfully loaded project.
       * Can also be used to completely reset the editor state.
       */
      .addCase(initializeEditor, (state, action) => {
        const { payload } = action;
        const { id, version, editable, navigationUrl, type, mobile_service_config } = payload;
        const descriptor = payload.descriptor as Descriptor;
        state.projectId = id;
        state.projectType = type;
        // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
        state.remoteDescriptor = Object.freeze(descriptor);
        state.currentDescriptor = Object.freeze(migrateDescriptor(descriptor));
        state.initialMigrationDiffPatch = createPatch(state.remoteDescriptor, state.currentDescriptor);
        state.version = version;
        state.editable = editable;
        state.undoStack = [];
        state.redoStack = [];
        state.mobileServiceConfig = mobile_service_config ? Object.freeze(mobile_service_config) : null;
        if (typeof navigationUrl !== 'undefined') {
          state.navigationPath = navigationUrl;
        }
      })
      .addCase(initializeEditorFailed, (state) => {
        state.failedToInitialize = true;
      })
      /**
       * Action dispatched when project saving starts.
       */
      .addCase(saveProjectPending, (state) => {
        state.savingVersion = state.version;
      })
      /**
       * Action dispatched when project saving has failed.
       */
      .addCase(saveProjectFailure, (state) => {
        state.savingVersion = null;
      })
      /**
       * Action dispatched when project save has successfully completed.
       */
      .addCase(saveProjectSuccess, (state, action) => {
        const { descriptor, version } = action.payload;
        state.savingVersion = null;
        // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
        state.remoteDescriptor = Object.freeze(descriptor);
        state.version = version;
        state.conflictVersion = null;
      })
      /**
       * Action for changing the current navigational state of the editor.
       */
      .addCase(navigateEditor, (state, action) => {
        state.navigationPath = action.payload;
      })
      /**
       * Action for editing the descriptor.
       * This change is pushed to the undo stack (or amended if applicable).
       */
      .addCase(editDescriptor, (state, action) => {
        const originalState = original(state) as EditorState<Descriptor>;
        if (!state.editable) {
          return;
        }
        const { currentDescriptor: originalDescriptor } = originalState;
        const change = action.payload;
        if (!originalDescriptor) {
          throw new Error(`Cannot apply changes to uninitialized descriptor`);
        }
        let descriptor: Descriptor = originalDescriptor;
        if (change.type === 'property') {
          const { path, value } = change;
          if (!path.length) {
            // Replace the whole descriptor
            descriptor = postProcessDescriptor(value as Descriptor, originalDescriptor);
          } else {
            descriptor = postProcessDescriptor(setPropertyByPath(descriptor, path, value), originalDescriptor);
          }
        } else {
          const { patch, ignoreErrors } = change;
          const patchedDescriptor = ignoreErrors
            ? applyPatchIgnoringErrors(originalDescriptor, patch)
            : applyPatch(originalDescriptor, patch);
          descriptor = postProcessDescriptor(patchedDescriptor, originalDescriptor);
        }
        const forwardPatch = getDiffPatch(originalDescriptor, descriptor);
        const backwardPatch = getDiffPatch(descriptor, originalDescriptor);
        if (!forwardPatch.length && !backwardPatch.length) {
          // No actual changes were made. This edit is actually no-op
          return;
        }
        // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
        state.currentDescriptor = Object.freeze(descriptor);
        // Redo stack must be cleared when any change is made
        state.redoStack = [];
        // Push a new undo stack entry, or amend the latest one if the change was made recently
        const { navigationPath } = state;
        const { timestamp } = change;
        const latestUndoEntry = state.undoStack.at(-1);
        if (
          latestUndoEntry &&
          timestamp - latestUndoEntry.timestamp < undoSplitThreshold &&
          navigationPath === latestUndoEntry.navigationPath
        ) {
          // Determine what the descriptor looked like before the latest undo entry
          const previousDescriptor = applyPatch(originalDescriptor, latestUndoEntry.backwardPatch);
          // Amend the latest undo entry
          latestUndoEntry.forwardPatch = getDiffPatch(previousDescriptor, descriptor);
          latestUndoEntry.backwardPatch = getDiffPatch(descriptor, previousDescriptor);
          // It is possible that the latest undo entry is now empty,
          // in which case it should be removed from the stack
          if (!latestUndoEntry.backwardPatch.length) {
            state.undoStack.pop();
          }
        } else {
          // Push a new undo stack entry
          state.undoStack.push({
            forwardPatch,
            backwardPatch,
            timestamp,
            navigationPath,
          });
          // Limit the undo stack length
          while (state.undoStack.length > maxUndoStackLength) state.undoStack.shift();
        }
      })
      /**
       * Action for undoing the latest change to the descriptor, if any.
       */
      .addCase(undoLatestChange, (state) => {
        const originalState = original(state)!;
        const { currentDescriptor } = originalState;
        const latestChange = state.undoStack.pop();
        if (latestChange) {
          const undoPatch = current(latestChange).backwardPatch;
          // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
          state.currentDescriptor = Object.freeze(applyPatch(currentDescriptor, undoPatch));
          state.redoStack.push(latestChange);
        }
      })
      /**
       * Action for redoing the latest undone change to the descriptor, if any.
       */
      .addCase(redoLatestUndo, (state) => {
        const originalState = original(state)!;
        const { currentDescriptor } = originalState;
        const latestChange = state.redoStack.pop();
        if (latestChange) {
          const redoPatch = current(latestChange).forwardPatch;
          // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
          state.currentDescriptor = Object.freeze(applyPatch(currentDescriptor, redoPatch));
          state.undoStack.push(latestChange);
        }
      })
      /**
       * Discards all the local changes done to the descriptor, making the state match the latest known remote state.
       * This also clears the undo & redo stacks.
       */
      .addCase(revertCurrentDescriptor, (state) => {
        const remoteDescriptor = current(state.remoteDescriptor) as Descriptor | null;
        if (remoteDescriptor) {
          // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
          state.currentDescriptor = Object.freeze(migrateDescriptor(remoteDescriptor));
          state.undoStack = [];
          state.redoStack = [];
          state.conflictVersion = null;
        }
      })
      /**
       * Merge incoming upstream changes done to the remote descriptor since the editor was loaded.
       * In practice this will be similar to a "rebase" in GIT: the local changes are tried to be re-applied
       * on top of the remote changes.
       *
       * If there are conflicts, then the local state is unaltered, meaning that the attempt to save
       * would possibly overwrite remote changes.
       */
      .addCase(rebaseEditorProject, (state, action) => {
        const { currentDescriptor: oldCurrentDescriptor, remoteDescriptor: oldRemoteDescriptor } = original(
          state,
        ) as EditorState<Descriptor>;
        const { version: newVersion, patch: remotePatch } = action.payload;
        if (!oldRemoteDescriptor || !oldCurrentDescriptor) {
          throw new Error('Cannot rebase on top of uninitialized remote project');
        }
        const currentChanges = getDiffPatch(oldRemoteDescriptor, oldCurrentDescriptor);
        const { base: newRemoteDescriptor, state: newCurrentDescriptor } = rebasePatch<Descriptor>(
          oldRemoteDescriptor,
          remotePatch,
          currentChanges,
        );
        state.version = newVersion;
        // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
        state.remoteDescriptor = Object.freeze(newRemoteDescriptor);
        if (newCurrentDescriptor) {
          // Successfully rebased
          // TODO: Need to check conflicts by automatic migrations and cleanups!
          // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
          state.currentDescriptor = Object.freeze(postProcessDescriptor(newCurrentDescriptor, oldCurrentDescriptor));
        } else {
          // There are conflicts!
          // IMPORTANT: Freezing the descriptor here prevents Immer doing a heavy finalization
          state.currentDescriptor = Object.freeze(migrateDescriptor(newRemoteDescriptor));
          // TODO: Leave the local state as is, but the save might overwrite remote changes, with warning!
          state.conflictVersion = newVersion;
        }
        // TODO: Preserve part of the undo stack that is still applicable
        state.undoStack = [];
        state.redoStack = [];
      })
      /**
       * Reset the initial migration diff patch, e.g. when the project is saved.
       */
      .addCase(resetInitialMigrationDiff, (state) => {
        state.initialMigrationDiffPatch = null;
      })

      .addCase(setMobileServiceConfig, (state, action) => {
        state.mobileServiceConfig = action.payload ? Object.freeze(action.payload) : null;
      }),
  );
  return reducer as Reducer<EditorState<Descriptor>>;
}
